/**
 * AmbassadorPayoutSetup - Vendor/Banking Details Form
 *
 * Ambassadors submit their banking details for commission payouts.
 * Admin reviews and creates Zoho vendor when approved.
 *
 * Status flow: (none) → submitted → approved / rejected
 *
 * URL: /ambassador/payout-setup
 */

import { useState, useEffect, type FormEvent, type CSSProperties } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import { POS_GOLD } from '../../config/posRoomStatus';


/** Vendor/payout detail status */
type VendorStatus = 'submitted' | 'approved' | 'rejected';

/** Full vendor details shape stored as JSONB on users.vendor_details */
interface VendorDetailsPayload {
  legal_name: string;
  id_number: string;
  vat_registered: boolean;
  vat_number: string | null;
  bank_name: string;
  bank_account_holder: string;
  bank_account_number: string;
  branch_code: string;
  bank_account_type: string;
  status: VendorStatus;
  submitted_at: string;
  reviewed_at: string | null;
  reviewed_by: string | null;
  rejection_reason: string | null;
  /** Legacy field aliases */
  account_holder?: string;
  account_number?: string;
  account_type?: string;
}

/** Form state for the payout setup form */
interface PayoutForm {
  legal_name: string;
  id_number: string;
  vat_registered: boolean;
  vat_number: string;
  bank_name: string;
  bank_account_holder: string;
  bank_account_number: string;
  branch_code: string;
  bank_account_type: string;
}

const BANK_OPTIONS = [
  'FNB',
  'ABSA',
  'Standard Bank',
  'Nedbank',
  'Capitec',
  'Investec',
  'TymeBank',
  'African Bank',
  'Other'
];

const ACCOUNT_TYPES = [
  { value: 'cheque', label: 'Cheque / Current' },
  { value: 'savings', label: 'Savings' },
  { value: 'transmission', label: 'Transmission' }
];

const STATUS_DISPLAY: Record<VendorStatus, { label: string; bg: string; color: string; icon: string }> = {
  submitted: { label: 'Under Review', bg: 'var(--color-info-bg)', color: 'var(--color-info-text)', icon: '⏳' },
  approved: { label: 'Approved', bg: 'var(--color-success-bg)', color: 'var(--color-success)', icon: '✓' },
  rejected: { label: 'Rejected', bg: 'var(--color-error-bg)', color: 'var(--color-error)', icon: '✗' }
};

function AmbassadorPayoutSetup() {
  const { supabase, user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [vendorDetails, setVendorDetails] = useState<VendorDetailsPayload | null>(null);

  const [form, setForm] = useState<PayoutForm>({
    legal_name: '',
    id_number: '',
    vat_registered: false,
    vat_number: '',
    bank_name: '',
    bank_account_holder: '',
    bank_account_number: '',
    branch_code: '',
    bank_account_type: 'cheque'
  });

  useEffect(() => {
    loadDetails();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- load on mount
  }, []);

  const loadDetails = async () => {
    try {
      setLoading(true);

      const { data, error: fetchError } = await supabase
        .from('users')
        .select('vendor_details')
        .eq('id', user!.id)
        .single();

      if (fetchError) throw fetchError;

      const vd = data?.vendor_details as VendorDetailsPayload | null;
      if (vd) {
        setVendorDetails(vd);
        setForm({
          legal_name: vd.legal_name || '',
          id_number: vd.id_number || '',
          vat_registered: vd.vat_registered || false,
          vat_number: vd.vat_number || '',
          bank_name: vd.bank_name || '',
          bank_account_holder: vd.bank_account_holder || vd.account_holder || '',
          bank_account_number: vd.bank_account_number || vd.account_number || '',
          branch_code: vd.branch_code || '',
          bank_account_type: vd.bank_account_type || vd.account_type || 'cheque'
        });
      }
    } catch (err: unknown) {
      console.error('Error loading vendor details:', err);
      setError('Failed to load your details');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!form.legal_name.trim()) return setError('Legal name is required');
    if (!form.id_number.trim()) return setError('ID or company registration number is required');
    if (form.vat_registered && !form.vat_number.trim()) return setError('VAT number is required');
    if (!form.bank_name) return setError('Bank name is required');
    if (!form.bank_account_holder.trim()) return setError('Bank account holder name is required');
    if (!form.bank_account_number.trim()) return setError('Bank account number is required');
    if (!form.branch_code.trim()) return setError('Branch code is required');

    try {
      setSaving(true);

      const details = {
        ...form,
        vat_number: form.vat_registered ? form.vat_number : null,
        status: 'submitted',
        submitted_at: new Date().toISOString(),
        reviewed_at: null,
        reviewed_by: null,
        rejection_reason: null
      };

      const { error: updateError } = await supabase
        .from('users')
        .update({ vendor_details: details })
        .eq('id', user!.id);

      if (updateError) throw updateError;

      setVendorDetails(details as VendorDetailsPayload);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      console.error('Error saving vendor details:', err);
      setError('Failed to save your details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field: keyof PayoutForm, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  if (loading) return <LoadingSpinner />;

  const status = vendorDetails?.status as VendorStatus | undefined;
  const isReadOnly = status === 'submitted' || status === 'approved';
  const statusInfo = status ? STATUS_DISPLAY[status] : null;

  return (
    <div className="page-content">
      <div style={{ maxWidth: '640px' }}>
        <h1 className="page-title mb-xs">Payout Setup</h1>
        <p style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-lg)', marginBottom: '24px' }}>
          Submit your banking details so we can process commission payments.
        </p>

        {/* Status Banner */}
        {statusInfo && (
          <div style={{
            background: statusInfo.bg,
            color: statusInfo.color,
            padding: '14px 20px',
            borderRadius: 'var(--radius)',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            fontWeight: 500
          }}>
            <span style={{ fontSize: 'var(--text-2xl)' }}>{statusInfo.icon}</span>
            <div>
              <span className="fw-600">{statusInfo.label}</span>
              {status === 'submitted' && (
                <span style={{ fontWeight: 400, marginLeft: '6px' }}>
                   — Your details are being reviewed. You&apos;ll be notified once approved.
                </span>
              )}
              {status === 'approved' && (
                <span style={{ fontWeight: 400, marginLeft: '6px' }}>
                   — Your payout details have been verified. Commissions will be processed automatically.
                </span>
              )}
              {status === 'rejected' && vendorDetails?.rejection_reason && (
                <span style={{ fontWeight: 400, marginLeft: '6px' }}>
                   — {vendorDetails.rejection_reason}
                </span>
              )}
            </div>
          </div>
        )}

        {saved && (
          <div style={{
            background: 'var(--color-success-bg)',
            color: 'var(--color-success)',
            padding: '12px 16px',
            borderRadius: 'var(--radius)',
            marginBottom: '20px',
            fontWeight: 500
          }}>
            Your details have been submitted for review.
          </div>
        )}

        {error && (
          <div className="alert alert-error mb-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Legal / Business Details */}
          <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 'var(--text-xl)', fontWeight: 600 }}>Business Details</h3>

            <div className="mb-lg">
              <label style={labelStyle}>Legal Name / Business Name</label>
              <input
                type="text"
                value={form.legal_name}
                onChange={(e) => updateField('legal_name', e.target.value)}
                placeholder="Full legal name or registered company name"
                style={inputStyle}
                disabled={isReadOnly}
              />
            </div>

            <div className="mb-lg">
              <label style={labelStyle}>ID Number / Company Registration</label>
              <input
                type="text"
                value={form.id_number}
                onChange={(e) => updateField('id_number', e.target.value)}
                placeholder="SA ID number or company registration number"
                style={inputStyle}
                disabled={isReadOnly}
              />
            </div>

            <div className="mb-lg">
              <label style={labelStyle}>VAT Registered?</label>
              <div className="flex gap-md">
                <button
                  type="button"
                  onClick={() => !isReadOnly && updateField('vat_registered', false)}
                  disabled={isReadOnly}
                  style={{
                    ...toggleBtnStyle,
                    background: !form.vat_registered ? POS_GOLD : 'var(--color-bg)',
                    color: !form.vat_registered ? 'var(--color-sidebar)' : 'var(--color-text-light)'
                  }}
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={() => !isReadOnly && updateField('vat_registered', true)}
                  disabled={isReadOnly}
                  style={{
                    ...toggleBtnStyle,
                    background: form.vat_registered ? POS_GOLD : 'var(--color-bg)',
                    color: form.vat_registered ? 'var(--color-sidebar)' : 'var(--color-text-light)'
                  }}
                >
                  Yes
                </button>
              </div>
            </div>

            {form.vat_registered && (
              <div>
                <label style={labelStyle}>VAT Number</label>
                <input
                  type="text"
                  value={form.vat_number}
                  onChange={(e) => updateField('vat_number', e.target.value)}
                  placeholder="e.g. 4123456789"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </div>
            )}
          </div>

          {/* Banking Details */}
          <div className="card" style={{ padding: '24px', marginBottom: '20px' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 'var(--text-xl)', fontWeight: 600 }}>Banking Details</h3>

            <div className="mb-lg">
              <label style={labelStyle}>Bank Name</label>
              <select
                value={form.bank_name}
                onChange={(e) => updateField('bank_name', e.target.value)}
                style={inputStyle}
                disabled={isReadOnly}
              >
                <option value="">Select bank...</option>
                {BANK_OPTIONS.map(bank => (
                  <option key={bank} value={bank}>{bank}</option>
                ))}
              </select>
            </div>

            <div className="mb-lg">
              <label style={labelStyle}>Bank Account Holder Name</label>
              <input
                type="text"
                value={form.bank_account_holder}
                onChange={(e) => updateField('bank_account_holder', e.target.value)}
                placeholder="Name as it appears on the bank statement"
                style={inputStyle}
                disabled={isReadOnly}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div>
                <label style={labelStyle}>Bank Account Number</label>
                <input
                  type="text"
                  value={form.bank_account_number}
                  onChange={(e) => updateField('bank_account_number', e.target.value)}
                  placeholder="Bank account number"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <label style={labelStyle}>Branch Code</label>
                <input
                  type="text"
                  value={form.branch_code}
                  onChange={(e) => updateField('branch_code', e.target.value)}
                  placeholder="e.g. 250655"
                  style={inputStyle}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            <div>
              <label style={labelStyle}>Bank Account Type</label>
              <select
                value={form.bank_account_type}
                onChange={(e) => updateField('bank_account_type', e.target.value)}
                style={inputStyle}
                disabled={isReadOnly}
              >
                {ACCOUNT_TYPES.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Submit Button */}
          {!isReadOnly && (
            <button
              type="submit"
              disabled={saving}
              style={{
                width: '100%',
                padding: '14px 32px',
                fontSize: 'var(--text-base)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                cursor: saving ? 'not-allowed' : 'pointer',
                background: saving ? 'var(--color-border)' : POS_GOLD,
                color: 'var(--color-sidebar)',
                fontFamily: 'inherit'
              }}
            >
              {saving ? 'Submitting...' : status === 'rejected' ? 'Resubmit Details' : 'Submit for Review'}
            </button>
          )}

          {isReadOnly && status !== 'approved' && (
            <p style={{ textAlign: 'center', color: 'var(--color-text-light)', fontSize: 'var(--text-base)', marginTop: '8px' }}>
              Contact CleanMyBed if you need to update your details.
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

const labelStyle: CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: 'var(--text-sm)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: 'var(--color-text-light)'
};

const inputStyle: CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  fontSize: 'var(--text-lg)',
  border: '2px solid rgba(0, 0, 0, 0.1)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--color-white)',
  fontFamily: 'inherit',
  boxSizing: 'border-box'
};

const toggleBtnStyle: CSSProperties = {
  padding: '8px 20px',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--text-base)',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit'
};

export default AmbassadorPayoutSetup;
